<!-- BaseModal.vue -->
<template>
    <transition name="modal-fade">
      <div
        v-if="isOpen"
        class="modal-overlay"
        :class="{ 'modal-overlay--fullscreen': fullScreen }"
        @click.self="emit('close')"
      >
        <div class="modal-box" :class="{ 'modal-box--fullscreen': fullScreen }">
          <div class="modal-header">
            <h3 class="modal-title">{{ title }}</h3>
            <button class="close-button" @click="emit('close')">×</button>
          </div>
          <div class="modal-body">
            <slot />
          </div>
          <div class="modal-footer">
            <button class="close-button-bottom" @click="emit('close')">Close</button>
          </div>
        </div>
      </div>
    </transition>
  </template>
  
  <script setup lang="ts">
  import { defineProps, defineEmits, watch, onUnmounted } from 'vue';
  import { iframeCommunication } from '@/utils/iframeCommunication';
  
  const props = defineProps<{
    isOpen: boolean
    title?: string
    fullScreen?: boolean
  }>()
  
  const emit = defineEmits(['close'])
  
  const isInIframe = iframeCommunication.isRunningInIframe()
  let savedScrollPosition = 0
  
  // Prevent body scrolling when modal is open
  const preventBodyScroll = () => {
    if (isInIframe) {
      document.body.classList.add('modal-open')
      document.documentElement.classList.add('modal-open')
      return
    }
    savedScrollPosition = window.pageYOffset || document.documentElement.scrollTop
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${savedScrollPosition}px`
    document.body.style.width = '100%'
  }
  
  const restoreBodyScroll = () => {
    if (isInIframe) {
      document.body.classList.remove('modal-open')
      document.documentElement.classList.remove('modal-open')
      return
    }
    document.body.style.overflow = ''
    document.body.style.position = ''
    document.body.style.top = ''
    document.body.style.width = ''
    window.scrollTo({
      top: savedScrollPosition,
      behavior: 'auto'
    })
  }
  
  watch(() => props.isOpen, (isOpen) => {
    if (isOpen) {
      if (isInIframe) {
        iframeCommunication.sendScrollToTop()
      }
      preventBodyScroll()
    } else {
      restoreBodyScroll()
    }
  })
  
  onUnmounted(() => {
    restoreBodyScroll()
  })
  </script>

  <script lang="ts">
  export default {
    name: 'BaseModal',
  }
  </script>
  
  <style scoped>
  .modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: var(--shadow-color);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    z-index: 9999;
    overflow-y: auto;
  }

  .modal-overlay--fullscreen {
    align-items: stretch;
    padding: 0;
  }
  
  .modal-box {
    background-color: var(--bg-card, var(--bg-secondary));
    width: 100%;
    max-width: 1280px;
    min-width: 320px;
    padding: 8px;
    position: relative;
    color: var(--text-primary);
    min-height: auto;
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    box-sizing: border-box;
  }

  .modal-box--fullscreen {
    max-width: none;
    width: 100%;
    min-height: 100vh;
    max-height: 100vh;
    border-radius: 0;
  }

  /* Iframe compatibility overrides */
  :global(body.iframe-mode) .modal-box--fullscreen {
    min-height: auto !important;
    max-height: none !important;
    height: auto !important;
  }
  
  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    flex-shrink: 0;
  }
  
  .modal-body {
    flex: 1;
    overflow-y: auto;
  }
  
  .modal-footer {
    display: flex;
    justify-content: center;
    padding-top: 16px;
    margin-top: 16px;
    border-top: 1px solid var(--border-color);
    flex-shrink: 0;
  }
  
  .modal-title {
    margin: 0;
    font-size: 18px;
    color: var(--accent-color-text-heading, var(--text-primary));
  }
  
  .close-button {
    background: none;
    border: none;
    font-size: 24px;
    color: var(--text-secondary);
    cursor: pointer;
    padding: 0;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .close-button-bottom {
    background: var(--accent-color);
    border: none;
    color: var(--text-button, var(--text-white));
    padding: 12px 32px;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s, opacity 0.2s;
  }
  
  .close-button-bottom:hover {
    opacity: 0.9;
    background: var(--bg-button-hover);
  }
  
  .close-button-bottom:active {
    opacity: 0.8;
  }
  
  /* Mobile specific styles */
  @media (max-width: 768px) {
    .modal-overlay {
      align-items: flex-start;
      padding: 0;
    }
    
    .modal-box {
      min-height: 100vh;
      max-height: 100vh;
      border-radius: 0;
      padding: 0;
      max-width: 100%;
      margin: 0;
    }
  }
  
  /* Desktop styles */
  @media (min-width: 769px) {
    .modal-overlay:not(.modal-overlay--fullscreen) {
      align-items: center;
      padding: 8px;
    }
    
    .modal-box:not(.modal-box--fullscreen) {
      min-height: auto;
      max-height: 90vh;
      border-radius: 8px;
      width: 100%;
      max-width: 1280px;
      padding: 8px;
    }
  }

  :global(body.iframe-mode) .modal-box {
    margin: 0 auto !important;
    height: auto !important;
    min-height: 0 !important;
  }
  </style>
  